import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink } from 'lucide-react';
import { PlatformItem } from '../types';

interface CaseStudyModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: PlatformItem | null;
}

const CaseStudyModal: React.FC<CaseStudyModalProps> = ({ isOpen, onClose, project }) => {
  if (!project) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-0 md:p-8 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0a0a0a] w-full max-w-7xl h-full md:h-auto md:max-h-[95vh] md:rounded-2xl overflow-hidden flex flex-col border border-white/10 relative"
          >
            
            {/* Close Button */}
            <button 
                onClick={onClose}
                className="absolute top-4 right-4 z-50 p-3 bg-black/50 backdrop-blur-md rounded-full text-white hover:text-brand-accent transition-colors border border-white/10 hover:border-brand-accent group"
            >
                <X size={24} className="group-hover:rotate-90 transition-transform" />
            </button>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
                
                {/* Hero Media */}
                <div className="w-full aspect-video bg-black relative">
                    {project.fullVideoId ? (
                        <iframe 
                            src={`https://player.vimeo.com/video/${project.fullVideoId}?autoplay=1&title=0&byline=0&portrait=0`} 
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0" 
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    ) : project.videoUrl ? (
                         <video 
                            src={project.videoUrl} 
                            controls 
                            className="w-full h-full object-contain"
                         />
                    ) : (
                        <img src={project.image} className="w-full h-full object-cover opacity-50" />
                    )}
                </div>

                <div className="p-6 md:p-12 lg:p-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    
                    {/* Content */}
                    <div className="lg:col-span-8">
                        <h1 className="text-4xl md:text-6xl font-display font-bold uppercase tracking-tighter mb-6 text-white">
                            {project.name}
                        </h1>
                        <p className="text-xl md:text-2xl font-display text-gray-300 leading-relaxed mb-8">
                            {project.description || "No description available for this project."}
                        </p>
                        
                        {/* Process / Gallery Placeholder */}
                        {project.gallery && (
                            <div className="space-y-8 mt-12">
                                <h3 className="text-sm font-mono uppercase tracking-widest text-brand-accent">Process & Frames</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {project.gallery.map((img, idx) => (
                                        <div key={idx} className="rounded-lg overflow-hidden border border-white/5">
                                            <img src={img} alt={`Process ${idx}`} className="w-full h-auto" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar / Credits */}
                    <div className="lg:col-span-4 space-y-8">
                        
                        <div className="bg-[#111] p-8 rounded-xl border border-white/5 space-y-6">
                            <div>
                                <h4 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-1">Role</h4>
                                <p className="text-lg text-white font-medium">{project.role}</p>
                            </div>
                            
                            {project.credits && (
                                <>
                                {project.credits.client && (
                                    <div>
                                        <h4 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-1">Client</h4>
                                        <p className="text-lg text-white font-medium">{project.credits.client}</p>
                                    </div>
                                )}
                                {project.credits.agency && (
                                    <div>
                                        <h4 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-1">Agency</h4>
                                        <p className="text-lg text-white font-medium">{project.credits.agency}</p>
                                    </div>
                                )}
                                </>
                            )}

                             <div>
                                <h4 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-1">Type</h4>
                                <p className="text-lg text-white font-medium">{project.stat}</p>
                            </div>
                        </div>

                        <a 
                            href={project.link}
                            target="_blank"
                            rel="noreferrer"
                            className="block w-full text-center bg-white text-black font-bold uppercase tracking-widest py-4 rounded-xl hover:bg-brand-accent transition-colors flex items-center justify-center gap-2"
                        >
                            View on {project.name} <ExternalLink size={16} />
                        </a>

                    </div>

                </div>

            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CaseStudyModal;